import { useEffect, useMemo, useState } from 'react'
import { getDashboardSummary, deleteEntry, getCashbookData } from '../../services/dashboardApi'
import { Wallet, Search, Users, CreditCard, ArrowUpRight, ArrowDownLeft } from 'lucide-react'

import { useLang } from '../../core/lang'
import LangKeys from '../../core/locales/keys'
import EntryDetailsView from '../parties/EntryDetailsView'
import AddEntryModal from '../parties/AddEntryModal'
import { FileText } from 'lucide-react'

function DashboardHome({ setActiveTab }) {
  const { t } = useLang()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [summary, setSummary] = useState({ balance: 0, total_give: 0, total_get: 0 })
  const [entries, setEntries] = useState([])
  const [cashSummary, setCashSummary] = useState({ total_balance: 0, today_balance: 0, cash_in_hand: 0, online_balance: 0, today_in: 0, today_out: 0 })
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editEntry, setEditEntry] = useState(null)
  const [addType, setAddType] = useState('give')
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    loadDashboard()
    loadCashbookData()
  }, [])

  const loadCashbookData = async () => {
    try {
      const res = await getCashbookData()
      if (res?.success) setCashSummary(res.data.summary || cashSummary)
    } catch (e) { console.error(e) }
  }

  const loadDashboard = async () => {
    try {
      setLoading(true); setError('')
      const res = await getDashboardSummary()
      if (res?.success) {
        setSummary({
          balance: Number(res?.data?.summary?.balance || 0),
          total_give: Number(res?.data?.summary?.total_give || 0),
          total_get: Number(res?.data?.summary?.total_get || 0),
        })
        setEntries(res?.data?.entries || [])
      }
    } catch (err) {
      setError(err.message || t(LangKeys.failed_load_dashboard))
    } finally { setLoading(false) }
  }

  const filteredEntries = useMemo(() => {
    if (!search.trim()) return entries
    const q = search.toLowerCase()
    return entries.filter((item) => item.party_name?.toLowerCase().includes(q) || item.mobile?.toLowerCase().includes(q))
  }, [entries, search])

  const formatCurrency = (value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value || 0)
  const formatDate = (value) => {
    if (!value) return '-'
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
  }

  const getAvatarStyle = (name) => {
    const colors = [{ bg: '#EFF6FF', text: '#1D4ED8' }, { bg: '#F0FDF4', text: '#15803D' }, { bg: '#FFF7ED', text: '#C2410C' }, { bg: '#FAF5FF', text: '#7E22CE' }, { bg: '#FDF2F8', text: '#BE185D' }, { bg: '#F0FDFA', text: '#0D9488' }];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  if (loading) return <div style={styles.page}><div style={styles.shimmerBoxLg} /><div style={styles.shimmerBoxSm} />{[1,2,3,4,5].map(i => <div key={i} style={styles.shimmerRow} />)}</div>
  if (error) return <div style={styles.errorBox}>{error}</div>

  return (
    <div style={styles.page}>
      <div style={{...styles.topCardsRow, flexDirection: isMobile ? 'column' : 'row'}}>
          <div style={{...styles.balanceCard, width: isMobile ? '100%' : '42%'}}>
            <div style={styles.cardHeaderRow}>
                <p style={styles.balanceLabel}>{t(LangKeys.outstanding_balance)}</p>
                <div style={{...styles.idIconBox, background: '#EFF6FF'}}><Users size={18} color="#2563EB" /></div>
            </div>
            <h2 style={styles.balanceAmount}>{formatCurrency(Math.abs(summary.balance))}</h2>
            <div style={styles.balanceDivider} />
            <div style={styles.balanceBottom}>
              <div><p style={styles.miniLabel}>{t(LangKeys.you_gave)}</p><h4 style={{...styles.miniAmount, color: '#DC2626'}}>{formatCurrency(summary.total_give)}</h4></div>
              <div><p style={styles.miniLabel}>{t(LangKeys.you_got)}</p><h4 style={{...styles.miniAmount, color: '#16A34A'}}>{formatCurrency(summary.total_get)}</h4></div>
            </div>
          </div>

          <div style={{...styles.summaryCardOrig, width: isMobile ? '100%' : 'auto'}}>
              <div style={styles.cardHeaderRow}>
                  <div style={styles.summaryTitleCol}>
                      <span style={styles.cardMainTitle}>{t(LangKeys.cashbook_status)}</span>
                      <p style={styles.cardSubTitle}>{t(LangKeys.real_time_cash_online)}</p>
                  </div>
                  <div style={{...styles.idIconBox, background: '#F0FDF4'}}><Wallet size={18} color="#16A34A" /></div>
              </div>
              <div style={styles.summaryGrid}>
                  <div style={styles.summaryItem}>
                      <span style={styles.label}>{t(LangKeys.total_balance).toUpperCase()}</span>
                      <h3 style={{...styles.value, color: '#16A34A'}}>{formatCurrency(cashSummary.total_balance)}</h3>
                      <div style={styles.subInfo}><span style={styles.dotCash}></span> {t(LangKeys.cash)}: {formatCurrency(cashSummary.cash_in_hand)}</div>
                      <div style={styles.subInfo}><span style={styles.dotOnline}></span> {t(LangKeys.online)}: {formatCurrency(cashSummary.online_balance)}</div>
                  </div>
                  <div style={styles.summaryItem}>
                      <span style={styles.label}>{t(LangKeys.today_balance).toUpperCase()}</span>
                      <h3 style={{...styles.value, color: cashSummary.today_balance >= 0 ? '#16A34A' : '#DC2626'}}>{formatCurrency(cashSummary.today_balance)}</h3>
                      <div style={{...styles.subInfo, color: '#16A34A'}}>+ {formatCurrency(cashSummary.today_in || 0)}</div>
                      <div style={{...styles.subInfo, color: '#DC2626'}}>- {formatCurrency(cashSummary.today_out || 0)}</div>
                  </div>
              </div>
              <button style={styles.viewReportBtn} onClick={() => setActiveTab('Cashbook')}>{t(LangKeys.view_report)}</button>
          </div>
      </div>

      <div style={{...styles.actionRow, width: isMobile ? '100%' : '42%'}}>
        <div style={styles.searchBox}>
            <Search size={18} color="#94A3B8" />
            <input type="text" placeholder={t(LangKeys.search_khata_entry)} style={styles.search} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button style={styles.lightBtn} onClick={() => setActiveTab('Cashbook')}>{t(LangKeys.cashbook)}</button>
      </div>

      <div style={{...styles.workspace, gridTemplateColumns: isMobile ? '1fr' : '42fr 58fr'}}>
        <div style={styles.leftPanel}>
          <div style={styles.panelHead}><h3 style={styles.panelTitle}>{t(LangKeys.recent_entries)}</h3></div>
          <div style={styles.listWrap}>
            {filteredEntries.length === 0 ? <div style={styles.emptyText}>{t(LangKeys.no_entries_found)}</div> : (
              filteredEntries.map((item) => {
                const isCredit = item.entry_type === 'get';
                const isActive = selectedEntry?.id === item.id;
                const partyName = item.party_name || t(LangKeys.unknown_customer);
                const colorStyle = getAvatarStyle(partyName);
                return (
                  <div key={item.id} onClick={() => setSelectedEntry(item)} style={{...styles.ledgerRow, ...(isActive ? styles.activeRow : {}) }}>
                    <div style={{...styles.avatar, background: colorStyle.bg, color: colorStyle.text}}>{partyName.charAt(0).toUpperCase()}</div>
                    <div style={styles.rowCenter}>
                      <div style={styles.rowName}>{partyName} {item.id < 0 && <span style={{marginLeft: '6px', color: '#F59E0B'}}>⚠</span>}</div>
                      <div style={styles.rowDate}>{formatDate(item.entry_date)}</div>
                    </div>
                    <div style={{...styles.rowAmount, color: isCredit ? '#16A34A' : '#DC2626'}}>{isCredit ? '+' : '-'} {formatCurrency(item.amount)}</div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {!isMobile && (
          <div style={{
            ...styles.rightPanel,
            padding: selectedEntry ? '0' : '18px',
            alignItems: selectedEntry ? 'stretch' : 'center',
            justifyContent: selectedEntry ? 'stretch' : 'center'
          }}>
            {selectedEntry ? (
              <EntryDetailsView entry={selectedEntry} party={{id: selectedEntry.customer_id, name: selectedEntry.party_name || t(LangKeys.unknown_customer), mobile: selectedEntry.mobile || '-'}} onBack={() => setSelectedEntry(null)} onRefresh={() => loadDashboard()} />
            ) : (
              <div style={styles.emptyState}>
                <div style={styles.emptyIllustration}><div style={styles.floatingIcon}><FileText size={56} color="#3B82F6" strokeWidth={1.5} /></div><div style={styles.iconShadow} /></div>
                <h3 style={styles.emptyTitle}>{t(LangKeys.select_entry)}</h3>
                <p style={styles.emptySub}>{t(LangKeys.entry_details_appear)}</p>
              </div>
            )}
          </div>
        )}
      </div>
      {showAddModal && <AddEntryModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} party={{id: selectedEntry?.customer_id, name: selectedEntry?.party_name, mobile: selectedEntry?.mobile}} type={addType} editEntry={editEntry} onSuccess={() => {setShowAddModal(false); loadDashboard();}} />}
    </div>
  )
}

const styles = {
  page: { padding: '16px', background: '#F8FAFC', minHeight: '100%' },
  topCardsRow: { display: 'flex', gap: '16px', marginBottom: '16px' },
  cardHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  idIconBox: { width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  summaryTitleCol: { display: 'flex', flexDirection: 'column' },
  cardMainTitle: { fontSize: '15px', fontWeight: '800', color: '#1E293B' },
  cardSubTitle: { fontSize: '11px', color: '#64748B', marginTop: '2px', fontWeight: '500' },
  balanceCard: { backgroundColor: '#fff', borderRadius: '20px', padding: '24px', border: '1px solid #E2E8F0', boxSizing: 'border-box', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  summaryCardOrig: { flex: 1, padding: '24px', background: '#FFF', border: '1px solid #E2E8F0', borderRadius: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', boxSizing: 'border-box' },
  balanceLabel: { fontSize: '14px', color: '#64748B', fontWeight: '600' },
  balanceAmount: { fontSize: '32px', fontWeight: '800', color: '#1D4ED8', marginTop: '4px' },
  balanceDivider: { height: '1px', backgroundColor: '#F1F5F9', margin: '18px 0' },
  balanceBottom: { display: 'flex', gap: '40px' },
  miniLabel: { fontSize: '13px', color: '#64748B', fontWeight: '500' },
  miniAmount: { fontSize: '18px', fontWeight: '700', marginTop: '4px' },
  actionRow: { display: 'flex', gap: '12px', marginBottom: '16px' },
  searchBox: { flex: 1, height: '48px', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '0 16px', display: 'flex', alignItems: 'center', gap: '12px', background: '#FFF' },
  search: { flex: 1, border: 'none', outline: 'none', fontSize: '14px', background: 'transparent' },
  lightBtn: { height: '48px', padding: '0 24px', borderRadius: '14px', border: '1px solid #E2E8F0', backgroundColor: '#FFF', fontWeight: '700', cursor: 'pointer', color: '#0F172A' },
  workspace: { display: 'grid', gap: '16px' },
  leftPanel: { backgroundColor: '#FFF', borderRadius: '20px', border: '1px solid #E2E8F0', height: '600px', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  panelHead: { padding: '20px', borderBottom: '1px solid #F1F5F9' },
  panelTitle: { fontSize: '16px', fontWeight: '800', color: '#0F172A', margin: 0 },
  listWrap: { flex: 1, overflowY: 'auto' },
  emptyText: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94A3B8', fontSize: '14px', fontWeight: '500' },
  ledgerRow: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px', cursor: 'pointer', borderBottom: '1px solid #F8FAFC' },
  activeRow: { backgroundColor: '#F8FAFC' },
  avatar: { width: '44px', height: '44px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '16px' },
  rowCenter: { flex: 1 },
  rowName: { fontSize: '15px', fontWeight: '700', color: '#0F172A', marginBottom: '4px' },
  rowDate: { fontSize: '12px', color: '#94A3B8', fontWeight: '500' },
  rowAmount: { fontSize: '16px', fontWeight: '800' },
  rightPanel: { backgroundColor: '#FFF', borderRadius: '20px', border: '1px solid #E2E8F0', height: '600px', position: 'sticky', top: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' },
  emptyState: { textAlign: 'center', padding: '40px' },
  emptyTitle: { fontSize: '20px', fontWeight: '800', color: '#0F172A', margin: '24px 0 8px 0' },
  emptySub: { fontSize: '14px', color: '#64748B', marginBottom: '32px' },
  summaryGrid: { display: 'flex', gap: '24px', marginBottom: '20px' },
  summaryItem: { flex: 1 },
  label: { fontSize: '11px', fontWeight: '800', color: '#94A3B8', display: 'block', marginBottom: '8px' },
  value: { fontSize: '22px', fontWeight: '900', margin: '0' },
  subInfo: { fontSize: '12px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', fontWeight: '600' },
  dotCash: { width: '8px', height: '8px', borderRadius: '50%', background: '#16A34A' },
  dotOnline: { width: '8px', height: '8px', borderRadius: '50%', background: '#2563EB' },
  viewReportBtn: { width: '100%', padding: '12px', background: '#FACC15', color: '#1E293B', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', fontSize: '14px' },
  shimmerBoxLg: { height: '180px', background: '#FFF', borderRadius: '20px', border: '1px solid #E2E8F0', marginBottom: '16px' },
  shimmerBoxSm: { height: '48px', background: '#FFF', borderRadius: '14px', border: '1px solid #E2E8F0', marginBottom: '16px' },
  shimmerRow: { height: '80px', background: '#FFF', borderRadius: '20px', border: '1px solid #E2E8F0', marginBottom: '12px' }
};

export default DashboardHome;